import { useEffect, useMemo, useRef, useCallback } from "react";

import { useThree, useFrame } from "@react-three/fiber";
import { createThrottledPresenceSender } from "../utils/presenceSendRate";
import { useObjectStore } from "../zustand/objects";
import { useAccountStore } from "../zustand/account";

export default function CaptureCursor({ currentObjectArtboard }) {
  const setPreview = useObjectStore((state) => state.setPreview);
  const sendPresenceUpdateToChannel = useObjectStore((state) => state.sendPresenceUpdateToChannel);
  const projectKey = useObjectStore((state) => state.projectKey);
  const { presence } = useObjectStore();
  const { firstName } = useAccountStore();

  const { realtimeToken } = useObjectStore();
  const { camera, raycaster, mouse } = useThree();

  const useMouseMove = (onMouseMove) => {
    useEffect(() => {
      document.addEventListener("pointermove", onMouseMove);
      return () => {
        document.removeEventListener("pointermove", onMouseMove);
      };
    }, [onMouseMove]);
  };

  useEffect(() => {
    setPreview(null);
  }, [setPreview]);

  const presenceRef = useRef(presence);
  presenceRef.current = presence;

  const presenceSender = useMemo(
    () =>
      createThrottledPresenceSender((payload) => {
        if (Object.keys(presenceRef.current).length > 0) {
          sendPresenceUpdateToChannel(payload);
        }
      }),
    [sendPresenceUpdateToChannel]
  );

  const ctxRef = useRef({});
  ctxRef.current = { projectKey, realtimeToken, firstName, currentObjectArtboard };

  /** Pointer moves can fire before R3F updates `mouse` / ray — send on the next useFrame with fresh three state. */
  const pointerDirty = useRef(false);
  const onPointerMove = useCallback(() => {
    pointerDirty.current = true;
  }, []);

  useMouseMove(onPointerMove);

  useFrame(() => {
    if (!pointerDirty.current) return;
    pointerDirty.current = false;
    if (Object.keys(presenceRef.current).length === 0) return;
    const c = ctxRef.current;
    presenceSender.push({
      field: "cursor",
      updateType: "presenceUpdate",
      projectKey: c.projectKey,
      realtime_token: c.realtimeToken,
      update: {
        cursor: {
          rotation: camera.rotation,
          position: raycaster.ray.origin,
          mouse,
          page: "editor",
          device: "computer",
          artboard: c.currentObjectArtboard,
          name: c.firstName,
        },
      },
    });
  });

  useEffect(() => {
    return () => presenceSender.cancel();
  }, [presenceSender]);

  useEffect(() => {
    if (Object.keys(presenceRef.current).length === 0) return;
    const c = ctxRef.current;
    presenceSender.force({
      field: "cursor",
      updateType: "presenceUpdate",
      projectKey: c.projectKey,
      realtime_token: c.realtimeToken,
      update: {
        cursor: {
          rotation: camera.rotation,
          position: raycaster.ray.origin,
          mouse,
          page: "editor",
          device: "computer",
          artboard: c.currentObjectArtboard,
          name: c.firstName,
        },
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- context only, not every pointer tick
  }, [currentObjectArtboard, projectKey, realtimeToken, firstName, presenceSender]);

  return "";
}
